import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { CSSProperties, ReactNode } from 'react';
import type { MessageDescriptor } from 'react-intl';
import { FormattedDate, FormattedMessage, FormattedNumber } from 'react-intl';
import type { To } from 'react-router';

import { Link } from '@sorare/routing';
import { css } from '@sorare/css';

import { Sport } from '#src/__generated__/globalTypes.ts';
import { Button } from '#src/atoms/buttons/Button/index.tsx';
import { Vertical } from '#src/atoms/layout/flex/index.tsx';
import {
  LinkBox,
  LinkOther,
  LinkOverlay,
} from '#src/atoms/navigation/Box/index.tsx';
import {
  BrandL,
  LabelL,
  LabelM,
  LabelS,
} from '#src/atoms/typography/index.tsx';
import { FrameCorners } from '#src/atoms/ui/FrameCorners/index.tsx';
import { Avatar } from '#src/components/user/Avatar/index.tsx';
import { useConfigContext } from '#src/contexts/config/index.ts';
import { useSportContext } from '#src/contexts/sport/index.ts';
import { useQuery } from '#src/hooks/graphql/useQuery.ts';
import { fantasy, glossary, navLabels } from '#src/lib/glossary.ts';
import { useCardSetBranding } from '#src/set/branding/useCardSetBranding.ts';

import type {
  SetManagerNavItemQuery,
  SetManagerNavItemQueryVariables,
} from './__generated__/index.graphql.ts';

const AVATAR_SIZE = 10;
const PREVIEW_HEIGHT = 284;

const preview = css`
  padding: var(--double-unit);
  border-radius: var(--double-unit);
  background-image:
    linear-gradient(
      180deg,
      rgba(var(--c-rgb-black), 0.2) 0%,
      var(--c-black) 100%
    ),
    var(--manager-profile-background, none);
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
`;

const stats = css`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  width: 100%;
`;

type StatProps = {
  value: ReactNode;
  label: MessageDescriptor;
};
const Stat = ({ value, label }: StatProps) => (
  <Vertical center gap={0.5}>
    <LabelM bold color="var(--c-onBase-secondary)">
      {value}
    </LabelM>
    <LabelS color="var(--c-onBase-tertiary)">
      <FormattedMessage {...label} />
    </LabelS>
  </Vertical>
);

const SET_MANAGER_NAV_ITEM_QUERY = gql`
  query SetManagerNavItemQuery($sport: Sport!, $cardSetSlug: String) {
    currentUser {
      slug
      nickname
      createdAt
      followersCount
      followingCount
      footballUserProfile {
        id
        clubName
      }
      nbaUserProfile {
        id
        clubName
      }
      setCards: searchCards(
        sport: $sport
        rarity: common
        cardSetSlug: $cardSetSlug
        pageSize: 1
      ) {
        nbHits
      }
      ...Avatar_publicUserInfoInterface
    }
  }
  ${Avatar.fragments.publicUserInfoInterface}
` as TypedDocumentNode<SetManagerNavItemQuery, SetManagerNavItemQueryVariables>;

type Props = { to: To; onClick?: () => void };
export const SetManagerNavItem = ({ to, onClick }: Props) => {
  const { sport } = useSportContext();
  const { cardSet } = useConfigContext();
  const { managerProfileBackgroundMobileUrl } = useCardSetBranding();
  const { data } = useQuery(SET_MANAGER_NAV_ITEM_QUERY, {
    variables: { sport, cardSetSlug: cardSet?.currentSet?.slug },
  });
  const { currentUser } = data || {};

  if (!currentUser) {
    return <div style={{ height: PREVIEW_HEIGHT }} />;
  }

  const { nickname, createdAt, followersCount, followingCount, setCards } =
    currentUser;

  const sportProfile = {
    [Sport.FOOTBALL]: currentUser.footballUserProfile,
    [Sport.NBA]: currentUser.nbaUserProfile,
    [Sport.BASEBALL]: null,
  }[sport];

  const cardsCount = cardSet?.currentSet ? setCards.nbHits : 0;

  const linkProps = { to, onClick };

  return (
    <LinkBox>
      <Vertical
        center
        gap={1.5}
        className={preview}
        style={
          {
            '--manager-profile-background':
              managerProfileBackgroundMobileUrl &&
              `url(${managerProfileBackgroundMobileUrl})`,
          } as CSSProperties
        }
      >
        <Vertical center gap={2}>
          <Avatar
            user={currentUser}
            size={AVATAR_SIZE}
            rounded
            hideChampionshipFrame
          />
          <Vertical center gap={0.5}>
            <FrameCorners
              color="var(--c-modes-arcade-tertiary)"
              className="px-2 py-1"
            >
              <BrandL color="var(--c-onBase-primary)">
                {sportProfile?.clubName || nickname}
              </BrandL>
            </FrameCorners>
            <LinkOverlay as={Link} {...linkProps}>
              <LabelL color="var(--c-onBase-secondary)">{nickname}</LabelL>
            </LinkOverlay>
          </Vertical>
        </Vertical>
        <div className={stats}>
          <Stat
            value={<FormattedNumber value={cardsCount} />}
            label={glossary.cards}
          />
          <Stat
            value={<FormattedNumber value={followersCount} />}
            label={glossary.followers}
          />
          <Stat
            value={<FormattedNumber value={followingCount} />}
            label={glossary.following}
          />
          <Stat
            value={
              <FormattedDate
                value={createdAt}
                month="2-digit"
                day="2-digit"
                year="2-digit"
              />
            }
            label={fantasy.joined}
          />
        </div>
        <LinkOther as="div">
          <Button color="quaternary" size="medium" {...linkProps}>
            <LabelL bold>
              <FormattedMessage {...navLabels.myClub} />
            </LabelL>
          </Button>
        </LinkOther>
      </Vertical>
    </LinkBox>
  );
};
